import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { API } from "@/lib/api";
import { X, ZoomIn } from "lucide-react";

/**
 * Renders illustrative image for a question if one exists.
 * Tap/click opens a full-screen lightbox for a closer look.
 * Silently renders nothing if there is no image.
 */
export default function QuestionImage({ qid, imageFile, className = "" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!imageFile) return null;
  const src = `${API}/questions/${qid}/image`;

  return (
    <div className={`mb-6 ${className}`} data-testid={`question-image-container-${qid}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`question-image-zoom-${qid}`}
        aria-label="Enlarge illustration"
        className="group relative inline-block rounded-md overflow-hidden border border-border bg-white cursor-zoom-in text-left"
      >
        <img
          data-testid={`question-image-${qid}`}
          src={src}
          alt="Question illustration"
          className="block max-h-64 w-auto"
          loading="lazy"
        />
        <span className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <ZoomIn className="w-4 h-4" />
        </span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
          onClick={() => setOpen(false)}
          data-testid={`image-lightbox-${qid}`}
        >
          <img
            src={src}
            alt="Question illustration enlarged"
            style={{ width: "min(880px, 94vw)", maxHeight: "88vh", objectFit: "contain" }}
            className="rounded-md bg-white p-2 shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`image-lightbox-close-${qid}`}
            aria-label="Close"
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
